import { useCallback } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { setAlertQueue } from 'cache/alerts'
import {
  clearPendingApprovals,
  pendingApprovalsVar,
  setPendingApprovals,
} from 'cache/pending-approvals'
import errorMessages from 'constants/errorMessages'
import GET_PENDING_APPROVALS, {
  HomepageDealsRequest,
  HomepageDealsResponse,
} from 'queries/get-pending-approvals'
import { MessageType } from 'ts/enums/messageType.enums'

type PendingApprovalsRefreshResult = {
  didError: boolean
}

export const usePendingApprovals = () => {
  const pendingApprovals = useReactiveVar(pendingApprovalsVar)

  const [getPendingApprovals, { data, loading, error }] = useLazyQuery<
    HomepageDealsResponse,
    HomepageDealsRequest
  >(GET_PENDING_APPROVALS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (response) => {
      const payload = response?.homepageDeals
      if (!payload) {
        clearPendingApprovals()
        return
      }

      setPendingApprovals({
        deals: payload.deals ?? [],
        didError: payload.didError,
        errorCode: payload.errorCode,
        errorMessage: payload.errorMessage,
        infoMessage: payload.infoMessage,
      })

      if (payload.didError) {
        setAlertQueue({
          type: MessageType.ERROR,
          message: payload.errorMessage ?? errorMessages.INTERNAL_SERVER_ERROR,
        })
      }
    },
    onError: (apolloError) => {
      console.error('Error fetching pending approvals:', apolloError)
      clearPendingApprovals()
      setAlertQueue({
        type: MessageType.ERROR,
        message: errorMessages.INTERNAL_SERVER_ERROR,
      })
    },
  })

  const handleGetPendingApprovals = useCallback(
    async (userId: string): Promise<PendingApprovalsRefreshResult> => {
      try {
        const response = await getPendingApprovals({
          variables: { userId },
        })

        return {
          didError: response?.data?.homepageDeals?.didError ?? true,
        }
      } catch (requestError) {
        return {
          didError: true,
        }
      }
    },
    [getPendingApprovals]
  )

  return {
    getPendingApprovals: handleGetPendingApprovals,
    data: data?.homepageDeals,
    pendingApprovals,
    loading,
    error,
  }
}
